// src/components/padrekko/qc/FindingCompletionSection.jsx
//
// TWO FACTS ABOUT ONE FINDING, rendered as two facts.
//
//   Remediation   the RESPONDENT'S report of their own work.
//                 "did the operator do it?" — operator org, operator_review.
//   Verification  the OWNER'S verdict on that report.
//                 "does the owner accept it?" — customer org.
//
// Both were once one column. 098 split them because they are statements by
// different parties and a reader has to be able to tell them apart: "operator
// says done" and "owner accepted it" are not the same claim, and neither is
// "operator says not done" the same as "owner rejected it". The vocabularies
// are disjoint on purpose (pending|completed|not_completed against
// unreviewed|verified|rejected) so a badge names its own axis.
//
// Each block follows the card's read-only convention independently: you see
// both states always, and you get the control only for the one your role owns.
// A customer never sees the remediation buttons; an operator never sees the
// verification buttons; both read both.

import { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Circle, ShieldCheck, ShieldX, ShieldQuestion } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";

const BADGE = {
  completed: { cls: "bg-emerald-500/15 border-emerald-500/50 text-emerald-300", label: "Completed", Icon: CheckCircle2 },
  not_completed: { cls: "bg-amber-500/15 border-amber-500/50 text-amber-300", label: "Not completed", Icon: AlertTriangle },
  pending: { cls: "bg-red-500/15 border-red-500/50 text-red-300", label: "Pending", Icon: Circle },
};

// A SEPARATE PALETTE AND SEPARATE WORDS, not a reuse of the one above. If
// "Verified" borrowed the completed badge a reader would have two identical
// green chips on one card meaning different things, which is the confusion the
// column split exists to remove.
const VERIFY_BADGE = {
  verified: { cls: "bg-sky-500/15 border-sky-500/50 text-sky-300", label: "Verified", Icon: ShieldCheck },
  rejected: { cls: "bg-rose-500/15 border-rose-500/50 text-rose-300", label: "Rejected", Icon: ShieldX },
  unreviewed: { cls: "bg-slate-500/10 border-slate-600/50 text-slate-400", label: "Unreviewed", Icon: ShieldQuestion },
};

function VerifyBadge({ status }) {
  const b = VERIFY_BADGE[status] ?? VERIFY_BADGE.unreviewed;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest
                      border rounded px-1.5 py-0.5 ${b.cls}`}>
      <b.Icon size={9} /> {b.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const b = BADGE[status] ?? BADGE.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest
                      border rounded px-1.5 py-0.5 ${b.cls}`}>
      <b.Icon size={9} /> {b.label}
    </span>
  );
}

export default function FindingCompletionSection({ finding }) {
  const currentStage = useQcStore((s) => s.caseInfo?.current_stage ?? null);
  const caseRole = useQcStore((s) => s.caseRole);
  const setFindingCompletion = useQcStore((s) => s.setFindingCompletion);
  const inflightId = useQcStore((s) => s.findingCompletionInflightId);
  const setFindingVerification = useQcStore((s) => s.setFindingVerification);
  const vInflightId = useQcStore((s) => s.findingVerificationInflightId);

  const canSet = currentStage === "operator_review" && caseRole === "operator_analyst";
  const status = finding.completion_status ?? "pending";
  const inflight = inflightId === finding.id;

  // THE OWNER'S CONTROL, on its own condition. Not gated on the stage: the
  // owner must be able to record a verdict on work done at operator_review,
  // which is a stage where the owner is not the writer. The server takes the
  // same view — the verification endpoint runs with enforceWriteable:false and
  // authorizes itself on the role alone. Only 'complete' closes it, and that is
  // checked server-side too.
  const canVerify = caseRole === "customer_analyst" && currentStage !== "complete";
  const vStatus = finding.verification_status ?? "unreviewed";
  const vInflight = vInflightId === finding.id;

  // form mode: null | 'completed' | 'not_completed'
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  const [party, setParty] = useState("");
  const [date, setDate] = useState("");

  // vMode: null | 'verified' | 'rejected'
  const [vMode, setVMode] = useState(null);
  const [vNote, setVNote] = useState("");
  const [vError, setVError] = useState(null);

  function openVerify(target) {
    setVNote(vStatus === target ? (finding.verification_note ?? "") : "");
    setVError(null);
    setVMode(target);
  }

  async function submitVerify() {
    if (!vNote.trim()) { setVError("A reason is required."); return; }
    setVError(null);
    const result = await setFindingVerification(finding.id, { status: vMode, note: vNote.trim() });
    if (result.ok) setVMode(null);
    else setVError(result.error || "Failed to save.");
  }

  async function resetVerify() {
    setVError(null);
    const result = await setFindingVerification(finding.id, { status: "unreviewed" });
    if (!result.ok) setVError(result.error || "Failed to reset.");
  }
  const [error, setError] = useState(null);

  function openForm(target) {
    // Prefill from the existing record when re-editing the current status.
    if (status === target) {
      setNote(finding.completion_note ?? "");
      setParty(finding.completion_responsible_party ?? "");
      // 098 SPLIT THE COLUMN AND THIS PRE-FILL WAS MISSED. The API stopped
      // returning `completion_date` and started returning completed_on /
      // target_date, so re-opening the form on an existing record pre-filled
      // an EMPTY date — silently, because `?? ""` is exactly what an absent
      // key produces. Read the half that matches the mode being edited.
      // `target`, not `mode` — this runs BEFORE setMode, so `mode` still holds
      // the previous value and would read the wrong half on the first open.
      setDate((target === "completed" ? finding.completed_on : finding.target_date) ?? "");
    } else {
      setNote(""); setParty(""); setDate("");
    }
    setError(null);
    setMode(target);
  }

  async function submit() {
    if (!note.trim() || !party.trim() || !date) {
      setError("Note, responsible party, and date are all required.");
      return;
    }
    setError(null);
    const result = await setFindingCompletion(finding.id, {
      status: mode, note: note.trim(), responsible_party: party.trim(), completion_date: date,
    });
    if (result.ok) setMode(null);
    else setError(result.error || "Failed to save.");
  }

  async function reset() {
    setError(null);
    const result = await setFindingCompletion(finding.id, { status: "pending" });
    if (!result.ok) setError(result.error || "Failed to reset.");
  }

  const metadata = status !== "pending" && (
    <div className="text-[9px] text-fg-secondary leading-snug mt-1 space-y-0.5">
      {finding.completion_note && <div className="text-slate-400">{finding.completion_note}</div>}
      <div>
        {finding.completion_responsible_party ? `${finding.completion_responsible_party} · ` : ""}
        {/* 098 split completion_date in two. The label has to follow, or the
            card shows a bare date whose meaning the reader has to infer from
            the badge beside it — which is what the single column did. */}
        {status === "completed" && finding.completed_on
          ? `Completed ${finding.completed_on}`
          : status === "not_completed" && finding.target_date
            ? `Target ${finding.target_date}`
            : ""}
      </div>
    </div>
  );

  return (
    <div className="mt-2 pt-2 border-t border-slate-800/40">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">Remediation</span>
        <StatusBadge status={status} />
      </div>

      {/* Read-only (UL / DH / operator after submit) — badge + metadata only. */}
      {!canSet && metadata}

      {/* Editable (operator @ operator_review). */}
      {canSet && !mode && (
        <>
          {metadata}
          <div className="flex items-center gap-1.5 mt-1.5">
            <button type="button" onClick={() => openForm("completed")} disabled={inflight}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20
                         disabled:opacity-40 transition">
              <CheckCircle2 size={11} /> {status === "completed" ? "Edit" : "Completed"}
            </button>
            <button type="button" onClick={() => openForm("not_completed")} disabled={inflight}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20
                         disabled:opacity-40 transition">
              <AlertTriangle size={11} /> {status === "not_completed" ? "Edit" : "Not completed"}
            </button>
            {status !== "pending" && (
              <button type="button" onClick={reset} disabled={inflight}
                className="text-[10px] px-2 py-1 text-fg-secondary hover:text-slate-300 disabled:opacity-40 transition">
                {inflight ? <Loader2 size={11} className="animate-spin" /> : "Reset"}
              </button>
            )}
          </div>
        </>
      )}

      {canSet && mode && (
        <div className="mt-1.5 space-y-1.5">
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={2} disabled={inflight}
            placeholder={mode === "completed" ? "What remediation was done…" : "Reason it isn't complete…"}
            className="w-full text-[10px] px-2 py-1 rounded border border-slate-700 bg-slate-900
                       text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-slate-500
                       disabled:opacity-50 leading-snug resize-none"
          />
          <input
            type="text" value={party} onChange={(e) => setParty(e.target.value)} disabled={inflight}
            placeholder="Responsible party"
            className="w-full text-[10px] px-2 py-1 rounded border border-slate-700 bg-slate-900
                       text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-slate-500 disabled:opacity-50"
          />
          <div className="flex items-center gap-1.5">
            <label className="text-[9px] text-fg-secondary whitespace-nowrap">
              {mode === "completed" ? "Date completed" : "Target date"}
            </label>
            <input
              type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={inflight}
              className="flex-1 text-[10px] px-2 py-1 rounded border border-slate-700 bg-slate-900
                         text-slate-100 focus:outline-none focus:border-slate-500 disabled:opacity-50"
            />
          </div>
          {error && <div className="text-[10px] text-red-300 leading-snug">{error}</div>}
          <div className="flex justify-end gap-1.5">
            <button type="button" onClick={() => { setMode(null); setError(null); }} disabled={inflight}
              className="text-[10px] px-2 py-1 rounded border border-slate-700 text-slate-300
                         hover:bg-slate-800 disabled:opacity-40 transition">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={inflight || !note.trim() || !party.trim() || !date}
              className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded border
                         bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4] hover:bg-[#16f2d4]/25
                         disabled:opacity-40 disabled:cursor-not-allowed transition">
              {inflight ? <Loader2 size={11} className="animate-spin" /> : null}
              {inflight ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Error surfaced from a reset action while no form is open. */}
      {canSet && !mode && error && <div className="text-[10px] text-red-300 mt-1">{error}</div>}

      {/* ── VERIFICATION — the owner's verdict, always shown, rarely editable ──
          Its own divider, its own heading, its own badge palette. The two blocks
          are siblings rather than one block with a second line, because they are
          statements by different parties and the card should read that way. */}
      <div className="mt-2 pt-2 border-t border-slate-800/40">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">Verification</span>
          <VerifyBadge status={vStatus} />
        </div>

        {/* Metadata is shown to EVERY role, including the operator, who cannot
            write it. An operator who cannot see that their work was rejected
            learns nothing from the rejection. */}
        {vStatus !== "unreviewed" && (
          <div className="text-[9px] text-fg-secondary leading-snug mt-1 space-y-0.5">
            {finding.verification_note && <div className="text-slate-400">{finding.verification_note}</div>}
            <div>
              {finding.verification_by_org_name ? `${finding.verification_by_org_name} · ` : ""}
              {finding.verification_updated_at
                ? new Date(finding.verification_updated_at).toISOString().slice(0, 10)
                : ""}
            </div>
          </div>
        )}

        {vStatus === "unreviewed" && !canVerify && (
          <div className="text-[9px] text-fg-secondary leading-snug mt-1">
            The owner has not reviewed this yet.
          </div>
        )}

        {canVerify && !vMode && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <button type="button" onClick={() => openVerify("verified")} disabled={vInflight}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         bg-sky-500/10 border-sky-500/40 text-sky-300 hover:bg-sky-500/20
                         disabled:opacity-40 transition">
              <ShieldCheck size={11} /> {vStatus === "verified" ? "Edit" : "Verify"}
            </button>
            <button type="button" onClick={() => openVerify("rejected")} disabled={vInflight}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         bg-rose-500/10 border-rose-500/40 text-rose-300 hover:bg-rose-500/20
                         disabled:opacity-40 transition">
              <ShieldX size={11} /> {vStatus === "rejected" ? "Edit" : "Reject"}
            </button>
            {vStatus !== "unreviewed" && (
              <button type="button" onClick={resetVerify} disabled={vInflight}
                className="text-[10px] px-2 py-1 text-fg-secondary hover:text-slate-300 disabled:opacity-40 transition">
                {vInflight ? <Loader2 size={11} className="animate-spin" /> : "Reset"}
              </button>
            )}
          </div>
        )}

        {canVerify && vMode && (
          <div className="mt-1.5 space-y-1.5">
            <textarea
              value={vNote} onChange={(e) => setVNote(e.target.value)} rows={2} disabled={vInflight}
              placeholder={vMode === "verified"
                ? "What was checked, and how…"
                : "Why this is being sent back…"}
              className="w-full text-[10px] px-2 py-1 rounded border border-slate-700 bg-slate-900
                         text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-slate-500
                         disabled:opacity-50 leading-snug resize-none"
            />
            {vError && <div className="text-[10px] text-red-300 leading-snug">{vError}</div>}
            <div className="flex justify-end gap-1.5">
              <button type="button" onClick={() => { setVMode(null); setVError(null); }} disabled={vInflight}
                className="text-[10px] px-2 py-1 rounded border border-slate-700 text-slate-300
                           hover:bg-slate-800 disabled:opacity-40 transition">
                Cancel
              </button>
              <button type="button" onClick={submitVerify} disabled={vInflight || !vNote.trim()}
                className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded border
                           bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4] hover:bg-[#16f2d4]/25
                           disabled:opacity-40 disabled:cursor-not-allowed transition">
                {vInflight ? <Loader2 size={11} className="animate-spin" /> : null}
                {vInflight ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}

        {canVerify && !vMode && vError && <div className="text-[10px] text-red-300 mt-1">{vError}</div>}
      </div>
    </div>
  );
}
